import type { CSSProperties } from "react";
import {
  participantColorByEmail,
  participantEmail,
  resolvePresenceLabel,
} from "@vida/wave-core";
import type { WaveParticipantRecord, WavePresenceRecord } from "@vida/wave-core";

type Props = {
  editors: WavePresenceRecord[];
  participants?: WaveParticipantRecord[];
  maxVisible?: number;
};

function editorLabel(
  editor: WavePresenceRecord,
  participants: WaveParticipantRecord[],
): string {
  return resolvePresenceLabel(editor, participants);
}

export function PresenceDots({
  editors,
  participants = [],
  maxVisible = 3,
}: Props) {
  if (editors.length === 0) return null;

  const visible = editors.slice(0, maxVisible);
  const overflow = editors.length - visible.length;

  return (
    <div
      className="wave-presence-dots-row"
      aria-live="polite"
      aria-label={visible
        .map((editor) => editorLabel(editor, participants))
        .join(", ")}
    >
      {visible.map((editor) => {
        const email = participantEmail(editor.sub, participants);
        const color = participantColorByEmail(email, editor.sub).hex;
        return (
          <span
            key={editor.sub}
            className="wave-presence-dots-cluster"
            style={{ ["--wave-user-color" as string]: color }}
            title={editorLabel(editor, participants)}
          >
            <span className="wave-presence-dots">
              <span className="wave-presence-dot" />
              <span className="wave-presence-dot" />
              <span className="wave-presence-dot" />
            </span>
          </span>
        );
      })}
      {overflow > 0 && (
        <span className="wave-presence-dots-overflow">+{overflow}</span>
      )}
    </div>
  );
}

export function primaryEditorColor(
  editors: WavePresenceRecord[],
  participants: WaveParticipantRecord[] = [],
): string | null {
  if (editors.length === 0) return null;
  const first = editors[0]!;
  return participantColorByEmail(participantEmail(first.sub, participants), first.sub).hex;
}

export function remoteEditingBlockStyle(
  editors: WavePresenceRecord[],
  participants: WaveParticipantRecord[] = [],
): CSSProperties | undefined {
  if (editors.length === 0) return undefined;
  if (editors.length === 1) {
    const editor = editors[0]!;
    const color = participantColorByEmail(
      participantEmail(editor.sub, participants),
      editor.sub,
    ).hex;
    return {
      ["--wave-user-color" as string]: color,
      borderColor: color,
      boxShadow: `0 0 0 2px color-mix(in srgb, ${color} 35%, transparent)`,
      background: `color-mix(in srgb, ${color} 10%, var(--wave-surface))`,
    };
  }
  const firstEditor = editors[0]!;
  const secondEditor = editors[1]!;
  const first = participantColorByEmail(
    participantEmail(firstEditor.sub, participants),
    firstEditor.sub,
  ).hex;
  const second = participantColorByEmail(
    participantEmail(secondEditor.sub, participants),
    secondEditor.sub,
  ).hex;
  return {
    ["--wave-user-color" as string]: first,
    borderImage: `linear-gradient(90deg, ${first}, ${second}) 1`,
    boxShadow: `0 0 0 2px color-mix(in srgb, ${first} 25%, transparent)`,
    background: `color-mix(in srgb, ${first} 8%, var(--wave-surface))`,
  };
}
